/**
 * useRestaurantTheme — Applies dynamic CSS variables from the restaurant's customStyle.
 *
 * Uses a <style> element injected into <head> so that:
 * - It overrides the default :root vars from index.css (same specificity, later in DOM)
 * - ThemeTestToggle can override it with inline styles (higher specificity)
 * - When inline overrides are removed, the restaurant theme shows through
 */
import { useEffect, useMemo } from 'react';
import { useTenant } from './TenantProvider';
import type { RestaurantCustomStyle } from '@shared/types/restaurant';

// ── Hex → HSL utilities ─────────────────────────────────────────────

export function hexToHsl(hex: string): { h: number; s: number; l: number } {
  hex = hex.replace('#', '');
  if (hex.length === 8) hex = hex.slice(0, 6);
  if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

export function hslStr(hex: string): string {
  const { h, s, l } = hexToHsl(hex);
  return `${h} ${s}% ${l}%`;
}

export function darken(hex: string, amount: number): string {
  const { h, s, l } = hexToHsl(hex);
  return `${h} ${s}% ${Math.max(0, l - amount)}%`;
}

export function lighten(hex: string, amount: number): string {
  const { h, s, l } = hexToHsl(hex);
  return `${h} ${Math.max(0, s - 10)}% ${Math.min(100, l + amount)}%`;
}

export function mutedHsl(hex: string, isDark: boolean): string {
  const { h } = hexToHsl(hex);
  return isDark ? `${h} 20% 20%` : `${h} 30% 92%`;
}

export function autoContrastText(mainColor: string): string {
  const { l } = hexToHsl(mainColor);
  return l > 55 ? '#1a1a1a' : '#FFFFFF';
}

// ── Contrast safety ─────────────────────────────────────────────────

const MIN_BRAND_CONTRAST = 30;

export function ensureContrast(brandHex: string, bgHex: string): string {
  const { h, s, l: brandL } = hexToHsl(brandHex);
  const bgL = hexToHsl(bgHex).l;

  if (Math.abs(brandL - bgL) >= MIN_BRAND_CONTRAST) {
    return `${h} ${s}% ${brandL}%`;
  }

  const isDarkBg = bgL < 50;
  const targetL = isDarkBg
    ? Math.min(100, bgL + MIN_BRAND_CONTRAST)
    : Math.max(0, bgL - MIN_BRAND_CONTRAST);

  return `${h} ${Math.max(s, 10)}% ${targetL}%`;
}

// ── Normalized config shape (what buildThemeCssVars expects) ────────

export interface ThemeConfig {
  mainColor: string;
  mainColorText: string;
  bodyColor: string;
  headerColor: string;
  textSubHeaderColor: string;
}

const LEGACY_BODY_COLOR_FALLBACK = '#b0b0b3';

export function normalizeCustomStyle(cs: RestaurantCustomStyle): ThemeConfig | null {
  if (!cs.mainColor) return null;
  return {
    mainColor: cs.mainColor,
    mainColorText: cs.mainColorText || autoContrastText(cs.mainColor),
    bodyColor: cs.bodyColor || LEGACY_BODY_COLOR_FALLBACK,
    headerColor: cs.headerColor || '#ffffff',
    textSubHeaderColor: cs.textSubHeaderColor || '#333333',
  };
}

// ── Build CSS variables from a theme config ─────────────────────────

export function buildThemeCssVars(style: ThemeConfig): Record<string, string> {
  const mc = style.mainColor;
  const hc = style.headerColor;
  const headerL = hexToHsl(hc).l;
  const isDarkCard = headerL < 40;

  // Brand contrast checked against headerColor (card bg) since brand elements live inside cards
  const safePrimary = ensureContrast(mc, hc);
  const safePrimaryL = parseInt(safePrimary.match(/(\d+)%$/)?.[1] || '50');
  const primaryFg = safePrimaryL > 55 ? '240 10% 10%' : '0 0% 100%';

  // Foreground derived from card (header) luminance — all visible text is inside cards
  const autoForeground = isDarkCard ? '0 0% 95%' : '240 10% 10%';

  const safeBrandDark = `${hexToHsl(mc).h} ${Math.max(hexToHsl(mc).s, 10)}% ${Math.max(0, safePrimaryL - 12)}%`;
  const safeBrandLight = `${hexToHsl(mc).h} ${Math.max(0, hexToHsl(mc).s - 10)}% ${Math.min(100, safePrimaryL + 15)}%`;

  return {
    '--primary': safePrimary,
    '--primary-foreground': primaryFg,
    '--primary-dark': safeBrandDark,
    '--primary-light': safeBrandLight,
    '--primary-muted': mutedHsl(mc, isDarkCard),
    '--background': hslStr(style.bodyColor),
    '--card': hslStr(hc),
    '--card-foreground': autoForeground,
    '--foreground': autoForeground,
    '--border': isDarkCard
      ? lighten(hc, 15)
      : `${hexToHsl(hc).h} 10% ${Math.max(headerL - 10, 0)}%`,
    '--input': isDarkCard
      ? lighten(hc, 15)
      : `${hexToHsl(hc).h} 10% ${Math.max(headerL - 10, 0)}%`,
    '--ring': safePrimary,
    '--accent': isDarkCard
      ? lighten(hc, 10)
      : `${hexToHsl(hc).h} 10% 95%`,
    '--accent-foreground': autoForeground,
    '--muted': isDarkCard
      ? lighten(hc, 5)
      : `${hexToHsl(hc).h} 5% 96%`,
    '--muted-foreground': hslStr(style.textSubHeaderColor),
    '--popover': hslStr(hc),
    '--popover-foreground': autoForeground,
    '--secondary': isDarkCard
      ? lighten(hc, 8)
      : `${hexToHsl(hc).h} 10% 96%`,
    '--secondary-foreground': autoForeground,
  };
}

export const THEME_CSS_VAR_KEYS = [
  '--primary', '--primary-foreground', '--primary-dark', '--primary-light', '--primary-muted',
  '--background', '--card', '--card-foreground', '--foreground', '--border',
  '--input', '--ring', '--accent', '--accent-foreground', '--muted', '--muted-foreground',
  '--popover', '--popover-foreground', '--secondary', '--secondary-foreground',
];

// ── Style element ID ────────────────────────────────────────────────

const STYLE_ELEMENT_ID = 'restaurant-theme';

function injectStyleElement(vars: Record<string, string>) {
  // Remove existing
  document.getElementById(STYLE_ELEMENT_ID)?.remove();

  const rules = Object.entries(vars).map(([k, v]) => `  ${k}: ${v};`).join('\n');
  const styleEl = document.createElement('style');
  styleEl.id = STYLE_ELEMENT_ID;
  styleEl.textContent = `:root {\n${rules}\n}`;
  document.head.appendChild(styleEl);
}

function removeStyleElement() {
  document.getElementById(STYLE_ELEMENT_ID)?.remove();
}

// ── Dynamic favicon ─────────────────────────────────────────────────

function setFavicon(url: string): () => void {
  const link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (!link) return () => {};
  const original = link.href;
  link.href = url;
  return () => { link.href = original; };
}

// ── Dynamic body background image ───────────────────────────────────

const BODY_BG_PROPS = ['backgroundImage', 'backgroundSize', 'backgroundPosition', 'backgroundAttachment', 'backgroundRepeat'] as const;

function setBodyImage(url: string): () => void {
  const { style } = document.body;
  style.backgroundImage = `url("${url}")`;
  style.backgroundSize = 'cover';
  style.backgroundPosition = 'center';
  style.backgroundAttachment = 'fixed';
  style.backgroundRepeat = 'no-repeat';
  return () => { for (const p of BODY_BG_PROPS) style.removeProperty(p.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)); };
}

// ── Hook ────────────────────────────────────────────────────────────

export function useRestaurantTheme(): ThemeConfig | null {
  const { restaurant } = useTenant();

  const config = useMemo(() => {
    if (!restaurant) return null;
    if (restaurant.designMode !== 'meitre-custom') return null;
    const cs = restaurant.customStyle;
    if (!cs) return null;
    return normalizeCustomStyle(cs);
  }, [restaurant]);

  // Inject CSS color variables
  useEffect(() => {
    if (!config) return;
    const vars = buildThemeCssVars(config);
    injectStyleElement(vars);
    return () => removeStyleElement();
  }, [config]);

  // Dynamic favicon
  const faviconUrl = restaurant?.customStyle?.faviconImage;
  useEffect(() => {
    if (!faviconUrl) return;
    return setFavicon(faviconUrl);
  }, [faviconUrl]);

  // Dynamic body background image
  const bodyImageUrl = restaurant?.customStyle?.bodyImage;
  useEffect(() => {
    if (!bodyImageUrl) return;
    return setBodyImage(bodyImageUrl);
  }, [bodyImageUrl]);

  return config;
}
